// MIT License - Copyright (c) 2026 Doug McNall

/**
 * Crew Personality System Configuration
 * 
 * Defines personality traits, preferences, and generation functions
 */

// Core personality traits (0-100 scale)
export const PERSONALITY_TRAITS = {
  BRAVE: {
    id: 'brave',
    name: 'Brave',
    description: 'Courage in dangerous situations',
    opposite: 'cautious'
  },
  CAUTIOUS: {
    id: 'cautious',
    name: 'Cautious',
    description: 'Careful and risk-averse',
    opposite: 'brave'
  },
  SOCIAL: {
    id: 'social',
    name: 'Social',
    description: 'Enjoys interaction with others',
    opposite: 'introverted'
  },
  INTROVERTED: {
    id: 'introverted',
    name: 'Introverted',
    description: 'Prefers solitude and quiet',
    opposite: 'social'
  },
  OPTIMISTIC: {
    id: 'optimistic',
    name: 'Optimistic',
    description: 'Positive outlook on situations',
    opposite: 'pessimistic'
  },
  PESSIMISTIC: {
    id: 'pessimistic',
    name: 'Pessimistic',
    description: 'Expects negative outcomes',
    opposite: 'optimistic'
  },
  TECHNICAL: {
    id: 'technical',
    name: 'Technical',
    description: 'Enjoys working with systems',
    opposite: 'creative'
  },
  CREATIVE: {
    id: 'creative',
    name: 'Creative',
    description: 'Artistic and imaginative',
    opposite: 'technical'
  }
};

// Crew preferences
export const CREW_PREFERENCES = {
  FOOD: ['Protein Bars', 'Synth Meals', 'Fresh Food', 'Exotic Cuisine'],
  ENTERTAINMENT: ['Holovids', 'Music', 'Games', 'Reading', 'Exercise'],
  QUARTERS: ['Private', 'Shared', 'Minimal', 'Luxurious'],
  DUTY_SHIFT: ['Alpha', 'Beta', 'Gamma', 'Flexible']
};

/**
 * Generate random personality profile
 * Creates balanced traits with some randomness
 */
export function generatePersonality() {
  const personality = {};
  
  // Generate opposing trait pairs (sum to ~100)
  const pairs = [
    ['brave', 'cautious'],
    ['social', 'introverted'], 
    ['optimistic', 'pessimistic'],
    ['technical', 'creative']
  ];
  
  pairs.forEach(([trait1, trait2]) => {
    const value1 = Math.floor(Math.random() * 80) + 10; // 10-90
    const value2 = Math.max(10, Math.min(90, 100 - value1 + (Math.random() * 20 - 10)));
    
    personality[trait1] = value1;
    personality[trait2] = value2;
  });
  
  return personality;
}

/**
 * Generate crew preferences
 */
export function generatePreferences() {
  return {
    food: CREW_PREFERENCES.FOOD[Math.floor(Math.random() * CREW_PREFERENCES.FOOD.length)],
    entertainment: CREW_PREFERENCES.ENTERTAINMENT[Math.floor(Math.random() * CREW_PREFERENCES.ENTERTAINMENT.length)],
    quarters: CREW_PREFERENCES.QUARTERS[Math.floor(Math.random() * CREW_PREFERENCES.QUARTERS.length)],
    dutyShift: CREW_PREFERENCES.DUTY_SHIFT[Math.floor(Math.random() * CREW_PREFERENCES.DUTY_SHIFT.length)]
  };
}

/**
 * Get dominant personality traits (top 2)
 */
export function getDominantTraits(personality) {
  return Object.entries(personality)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 2)
    .map(([trait]) => PERSONALITY_TRAITS[trait.toUpperCase()]?.name || trait);
}